import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { CartService, CartSummary, CartItemSummary } from '../../../services/cart.service';
import { AuthenticationService } from '../../../authenticationModule/services/authentication.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-cart-widget',
  templateUrl: './cart-widget.component.html',
  styleUrls: ['./cart-widget.component.scss']
})
export class CartWidgetComponent implements OnInit, OnDestroy {
  cart: CartSummary | null = null;
  panelVisible = false;
  private sub: Subscription | null = null;

  constructor(
    private readonly cartService: CartService,
    private readonly authenticationService: AuthenticationService,
    private readonly router: Router
  ) { }

  ngOnInit(): void {
    this.sub = new Subscription();
    this.sub.add(this.cartService.cart$.subscribe(cart => {
      this.cart = cart;
      if (!cart || cart.isEmpty) {
        this.panelVisible = false;
      }
    }));
    this.sub.add(this.authenticationService.authenticationEvents$.subscribe(user => {
      if (user) {
        this.cartService.refreshCart();
      } else {
        this.cartService.clearCart();
      }
    }));
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  togglePanel(): void {
    if (!this.cart || this.cart.isEmpty) {
      return;
    }
    this.panelVisible = !this.panelVisible;
  }

  closePanel(): void {
    this.panelVisible = false;
  }

  navigateToCart(): void {
    this.closePanel();
    this.router.navigate(['/cart']);
  }

  get itemCount(): number {
    if (!this.cart?.items?.length) {
      return 0;
    }

    return this.cart.items.reduce((total, item) => total + this.getQuantity(item), 0);
  }

  get cartItems(): CartItemSummary[] {
    return this.cart?.items ?? [];
  }

  get cartTotal(): string {
    const total = this.cart?.totals?.['total'];
    const currency = this.cart?.totals?.['currency'] || this.cart?.currency;
    if (typeof total !== 'number' || !currency) {
      return '';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency as string
    }).format(total);
  }

  formatPrice(pricing: CartItemSummary['pricing'] | null): string {
    if (!pricing || typeof pricing.amount !== 'number' || !pricing.currency) {
      return '-';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: pricing.currency
    }).format(pricing.amount);
  }

  formatLineTotal(item: CartItemSummary): string {
    const pricing = item.pricing;
    if (!pricing || typeof pricing.amount !== 'number' || !pricing.currency) {
      return '-';
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: pricing.currency
    }).format(pricing.amount * this.getQuantity(item));
  }

  itemTitle(item: CartItemSummary): string {
    const companyName = this.getCompanyName(item);
    const prefix = companyName ? `Extra XCO (ADDP) for ${companyName}` : 'ADDP';
    const pop = this.extractPop(item);
    const technical = this.extractTechnicalPop(item);
    const label = pop ? `${pop}${technical ? ` (${technical})` : ''}` : technical || '';
    return label ? `${prefix} @ ${label}` : prefix;
  }

  itemDescription(_item: CartItemSummary): string {
    return '';
  }

  private extractPop(item: CartItemSummary): string {
    return item.payload?.pop?.displayId
      || item.payload?.pop?.name
      || item.payload?.connectionPoint?.colocationSite
      || '';
  }

  private extractTechnicalPop(item: CartItemSummary): string | null {
    const technical = item.payload?.connectionPoint?.crossConnect?.technicalPop?.technicalId;
    if (typeof technical === 'string' && technical.trim().length > 0) {
      return technical.trim();
    }
    return null;
  }

  private getCompanyName(item: CartItemSummary): string | null {
    const name = item.payload?.company?.name;
    if (typeof name === 'string' && name.trim().length > 0) {
      return name.trim();
    }
    return null;
  }

  getQuantity(item: CartItemSummary): number {
    const quantity = Number(item.payload?.quantity ?? 1);
    if (!Number.isFinite(quantity)) {
      return 1;
    }

    return Math.max(1, Math.trunc(quantity));
  }
}
